<template>
    <div v-if="!currentWebAppName">
        <v-card class="mt-10 mx-auto" max-width="1280">
            <InstallationGuide/>
        </v-card>
    </div>
    <div v-else>
        <v-row class="mt-4" justify="space-between">
            <v-col cols="10">
                <h2 class="text-uppercase">
                    <WebAppSelection></WebAppSelection>
                    <span class="ml-4 text-subtitle-1 font-weight-bold">{{ getFriendlySelectedTimePeriodStart }} - {{ getFriendlySelectedTimePeriodEnd }}</span>
                    <span class="mr-5">
                        <TimePeriodSelection
                            buttonText="Last"
                            :defaultSelection="getTimeSelection"
                            v-on:onTimePeriodSelected="onTimePeriodSelected"/>
                    </span>
                    <TimePeriodSelection
                        buttonText="Contrast to"
                        :defaultSelection="getContrastTimeSelection"
                        v-on:onTimePeriodSelected="onContrastTimePeriodSelected"/>
                </h2>
            </v-col>
            <v-col cols="2">
                <AutoRefresh class="mt-n2" v-on:onRefresh="onRefresh"/>
            </v-col>
        </v-row>
        <!-- APPLICATION PERFORMANCE -->
        <v-row>
            <v-col>
                <v-icon size="36" color="grey">mdi-view-dashboard</v-icon>
                <span class="pl-2 text-h6 text-uppercase">Application Performance<span><v-btn :to="{ name: 'webTierDrilldown' }" text small class="text-uppercase" color="primary">recent transactions</v-btn></span></span>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="6">
                <v-row no-gutters>
                    <v-col>
                        <BarWithContrast
                            class="mx-5 my-2"
                            title="Average Response Time (web + blockchain)"
                            :mainValue="webAvgRT"
                            :contrastValue="webContrastAvgRT"
                            v-bind:contrastPeriodCode="getContrastTimeSelection"
                            unit="ms"
                            chartSize="large"
                            :relativeValue="relativeValue"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col>
                        <BarWithContrast
                            class="mx-5 my-2"
                            title="Average Response Time (blockchain only)"
                            :mainValue="bcAvgRT"
                            :contrastValue="bcContrastAvgRT"
                            v-bind:contrastPeriodCode="getContrastTimeSelection"
                            unit="ms"
                            :relativeValue="relativeValue"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="6">
                <BarWithContrast
                    class="mx-5 my-2"
                    title="Throughput"
                    :mainValue="webTP"
                    :contrastValue="webContrastTP"
                    v-bind:contrastPeriodCode="getContrastTimeSelection"
                    unit="txn/min"
                    chartSize="large"
                />
            </v-col>
        </v-row>
        <!-- BLOCKCHAIN PERFORMANCE -->
        <v-row class="my-3">
            <v-col>
                <v-icon size="36" color="grey">mdi-view-dashboard</v-icon>
                <span class="pl-2 text-h6 text-uppercase">Blockchain Performance</span>
            </v-col>
        </v-row>
        <v-row v-for="(channelGroup, idx1) in channelsInGroup" :key="idx1" no-gutters class="px-3 pb-6">
            <v-col v-for="(channel, idx2) in channelGroup" :key="idx2" cols="6">
                <v-card  flat tile outlined class="mx-3 elevation-4" height="256">
                    <v-btn @click="handleChannelClick" :data-channel-name="channel" text large class="text-h6 text-uppercase" color="primary">{{ channel }}</v-btn>
                    <v-row no-gutters>
                        <v-col cols="3">
                            <d3-network v-if="channelTopologyData[channel]" :net-nodes="generateNodes(channel)" :net-links="generateLinks(channel)" :options="nodeOptions" />
                        </v-col>
                        <v-col cols="9">
                            <v-row no-gutters>
                                <v-col v-if="channelVitalData[channel]">
                                    <BarWithContrast
                                        class="my-3"
                                        title="Average Execution Time"
                                        :mainValue="channelVitalData[channel].current.avg_response_time"
                                        :contrastValue="channelVitalData[channel].contrast.avg_response_time"
                                        v-bind:contrastPeriodCode="getContrastTimeSelection"
                                        unit="ms"
                                        chartSize="small"
                                    />
                                    <BarWithContrast
                                        class="my-3"
                                        title="Throughput"
                                        :mainValue="channelVitalData[channel].current.tp_per_min"
                                        :contrastValue="channelVitalData[channel].contrast.tp_per_min"
                                        v-bind:contrastPeriodCode="getContrastTimeSelection"
                                        unit="exec/min"
                                        chartSize="small"
                                    />
                                    <BarWithContrast
                                        class="my-3"
                                        title="Success Rate"
                                        :mainValue="channelVitalData[channel].current.success_rate"
                                        :contrastValue="channelVitalData[channel].contrast.success_rate"
                                        v-bind:contrastPeriodCode=getContrastTimeSelection
                                        unit="%"
                                        chartSize="small"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>        

        </v-row>                
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import Api from '@/services/api'
    import NumberHelper from '@/helpers/number_helper'
    import TopologyHelper from '@/helpers/topology_helper'
    import D3Network from 'vue-d3-network'

    import AutoRefresh from '@/components/AutoRefresh.vue'
    import TimePeriodSelection from '@/components/TimePeriodSelection.vue'
    import WebAppSelection from '@/components/WebAppSelection.vue'

    import BarWithContrast from '@/components/BarWithContrast.vue'
    import InstallationGuide from '@/components/InstallationGuide.vue'

    export default {

        components: {
            TimePeriodSelection, D3Network, BarWithContrast, AutoRefresh, InstallationGuide, WebAppSelection
        },

        name: 'PDDashboard',

        watch: {
            currentWebAppName: function (newWebAppName, oldWebAppName) {
                this.getWebVitalData();
                this.getContrastWebVitalData();
                this.getBlockchainVitalData();
                this.getContrastBlockchainVitalData();
                this.getChannelList();
            }
        },

        mounted: function () {
            if (this.hasSubscription &&  this.currentWebAppName) {
                this.getWebVitalData();
                this.getContrastWebVitalData();
                this.getBlockchainVitalData();
                this.getContrastBlockchainVitalData();
                this.getChannelList();
            } else {
                console.log("No subscription or data collectors. Data fetching is stopped.")
            }
        },

        computed: {
            ...mapGetters([
                'webAppNames',
                'currentWebAppName',
                'channels',
                'selectedTimePeriodStart',
                'selectedTimePeriodEnd',
                'contrastTimePeriodStart',
                'contrastTimePeriodEnd',
                'getTimeSelection',
                'getContrastTimeSelection',
                'getFriendlySelectedTimePeriodStart',
                'getFriendlySelectedTimePeriodEnd',
            ]),
            ...mapGetters('auth', {
                hasSubscription: 'hasSubscription'
            }),            
            relativeValue: function() {
                return Math.max(this.webAvgRT, this.webContrastAvgRT, this.bcAvgRT, this.bcContrastAvgRT )
            },
            channelsInGroup: function() {
                return this.channels.chunkArray(2)
            }
        },

        methods: {
        
            ...mapActions([
                'setChannels',
                'setCurrentChannel',
                'setTimeSelection',
                'setContrastTimeSelection']),      

            generateNodes: function(channelName) {
                if ( this.channelTopologyData[channelName]['endorsers'].length > 0 || this.channelTopologyData[channelName]['orderers'] > 0 ) {
                    return TopologyHelper.generateNodes(this.channelTopologyData[channelName]['endorsers'], this.channelTopologyData[channelName]['orderers'])
                } else {
                    return [];
                }
            },

            generateLinks: function(channelName) {
                if ( this.channelTopologyData[channelName]['endorsers'].length > 0 || this.channelTopologyData[channelName]['orderers'] > 0 ) {
                    return TopologyHelper.generateLinks(this.channelTopologyData[channelName]['endorsers'], this.channelTopologyData[channelName]['orderers'])
                } else {
                    return [];
                }
            },

            onTimePeriodSelected: function(numOfMinutesAgo) {
                console.log("onTimePeriodSelected callback received")
                this.setTimeSelection(numOfMinutesAgo);
                this.getWebVitalData();
                this.getBlockchainVitalData();
                this.getChannelList();
            },

            onContrastTimePeriodSelected: function(numOfMinutesAgo) {
                console.log("onContrastTimePeriodSelected callback received")
                this.setContrastTimeSelection(numOfMinutesAgo);
                this.getContrastWebVitalData();
                this.getContrastBlockchainVitalData();
                this.getChannelList();
            },

            onRefresh: function() {
                this.setTimeSelection(this.getTimeSelection);
                this.setContrastTimeSelection(this.getContrastTimeSelection);
                this.getWebVitalData();
                this.getContrastWebVitalData();
                this.getBlockchainVitalData();
                this.getContrastBlockchainVitalData();
                this.getChannelList();
            },
            
            handleChannelClick: function(event) {
                let channelName = event.currentTarget.getAttribute('data-channel-name');
                this.setCurrentChannel( channelName )
                this.$router.push({name: 'blockchainTierDrilldown'});
            },

            getWebVitalData: function() {
                Api.webRequests.getVitalData(
                    {
                        time_period_start: this.selectedTimePeriodStart,
                        time_period_end: this.selectedTimePeriodEnd,
                        app_name: this.currentWebAppName
                    }
                )
                .then((response) => {
                    console.log("Web vital data: ", response.data)
                    this.webAvgRT = response.data.avg_response_time
                    this.webTP = response.data.tp_per_min
                })
            },

            getContrastWebVitalData: function() {
                Api.webRequests.getVitalData(
                    {
                        time_period_start: this.contrastTimePeriodStart,
                        time_period_end: this.contrastTimePeriodEnd,
                        app_name: this.currentWebAppName
                    }
                )
                .then((response) => {
                    console.log("Contrast web vital data: ", response.data)
                    this.webContrastAvgRT = response.data.avg_response_time
                    this.webContrastTP = response.data.tp_per_min
                })
            },

            getBlockchainVitalData: function() {
                Api.blockchainRequests.getVitalData(
                    {
                        time_period_start: this.selectedTimePeriodStart,
                        time_period_end: this.selectedTimePeriodEnd,
                        app_name: this.currentWebAppName
                    }
                )
                .then((response) => {
                    console.log("Blockchain vital data: ", response.data)
                    this.bcAvgRT = response.data.avg_response_time
                })
            },

            getContrastBlockchainVitalData: function() {
                Api.blockchainRequests.getVitalData(
                    {
                        time_period_start: this.contrastTimePeriodStart,
                        time_period_end: this.contrastTimePeriodEnd,
                        app_name: this.currentWebAppName
                    }
                )
                .then((response) => {
                    console.log("Contrast blockchain vital data: ", response.data)
                    this.bcContrastAvgRT = response.data.avg_response_time
                })
            },

            getChannelToplogy: function(channelName) {
                this.$set(this.channelTopologyData, channelName, { endorsers: [], orderers: [] })
                Api.channels.getTopology({ app_name: this.currentWebAppName, channel_name: channelName })
                .then((response) => {
                    console.log("Channel topology data for channel '%s'", channelName, response.data)
                    this.$set(this.channelTopologyData[channelName], 'endorsers', response.data.endorsers)
                    this.$set(this.channelTopologyData[channelName], 'orderers', response.data.orderers)
                })
            },

            getChannelVitalData: function(channelName) {
                this.$set(this.channelVitalData, channelName, {current: {}, contrast:{}})
                Api.channels.getVitalDataByChannel(
                    {
                        time_period_start: this.selectedTimePeriodStart,
                        time_period_end: this.selectedTimePeriodEnd,
                        app_name: this.currentWebAppName,
                        channel_name: channelName
                    }
                )
                .then((response) => {
                    console.log("Channel vital data for channel '%s'", channelName, response.data)
                    this.channelVitalData[channelName]['current'] = response.data
                })
            },

            getContrastChannelVitalData: function(channelName) {
                Api.channels.getVitalDataByChannel(
                    {
                        time_period_start: this.contrastTimePeriodStart,
                        time_period_end: this.contrastTimePeriodEnd,
                        app_name: this.currentWebAppName,
                        channel_name: channelName                    
                    }
                )
                .then((response) => {
                    console.log("Contrast channel vital data for channel '%s'", channelName, response.data)
                    this.channelVitalData[channelName]['contrast'] = response.data
                })
            },

            getChannelList: function() {
                Api.channels.getChannelList({            
                    app_name: this.currentWebAppName
                })
                .then((response) => {
                    console.log("Channel list:", response.data)
                    const channels = response.data.map(element => { return element.channel_name })
                    this.setChannels(channels)
                    for (const channelName of channels) {
                        this.getChannelVitalData(channelName)
                        this.getContrastChannelVitalData(channelName)
                        this.getChannelToplogy(channelName)
                    }
                })
            },
        
            generateTestData: function() {
                this.webAvgRT = NumberHelper.getRandomInteger(100, 300)
                this.webContrastAvgRT = NumberHelper.getRandomInteger(100, 300)
                this.webTP = NumberHelper.getRandomInteger(20, 80)
                this.webContrastTP = NumberHelper.getRandomInteger(20, 80)
                this.bcAvgRT = NumberHelper.getRandomInteger(100, 300)
                this.bcContrastAvgRT = NumberHelper.getRandomInteger(100, 300)
                for (const channel of this.channels) {
                    this.channelVitalData[channel]['current'] = {
                        avg_response_time: NumberHelper.getRandomInteger(100, 300),
                        tp_per_min: NumberHelper.getRandomInteger(20, 80),
                        success_rate: NumberHelper.getRandomInteger(0, 100)
                    }
                    this.channelVitalData[channel]['contrast'] = {
                        avg_response_time: NumberHelper.getRandomInteger(100, 300),
                        tp_per_min: NumberHelper.getRandomInteger(20, 80),
                        success_rate: NumberHelper.getRandomInteger(0, 100)
                    }
                }
            }
        
        },

        data: () => ({
            webAvgRT: 0,
            webContrastAvgRT: 0,
            webTP: 0,
            webContrastTP: 0,
            bcAvgRT: 0,
            bcContrastAvgRT: 0,
            channelVitalData: {},
            channelTopologyData: {},
            tab: 0,

            nodeOptions: {
                force: 220,
                size:{ w:200, h:200},
                nodeSize: 12,
                nodeLabels: false,
                linkLabels: false,
                canvas: false
            }
        }),
    }
</script>

<style src="vue-d3-network/dist/vue-d3-network.css"></style>
<style scoped lang="scss">

</style>